
import { useState, useEffect } from "react";
import SingleTimeCapsule from "./singleTimeCapsule";
import ModuleHeader from "../../moduleHeader";
import { getCurrentPackageUserModule, getCurrentPackageUser, setCurrentPackage } from "../../../services/api";
import { getTimeCapsuleList, getTimeCapsule, postTimeCapsule, postTimeCapsulePhoto, updateTimeCapsuleStatus } from "../../../services/timeCapsuleService";
import { FaPowerOff, FaCopy, FaInfoCircle } from 'react-icons/fa';
import { LuRectangleHorizontal } from "react-icons/lu";
import Notiflix from "notiflix";

export default function TimeCapsule({ id, moduleId, isOwner }) {
  const [showSingleTimeCapsule, setShowSingleTimeCapsule] = useState(false);
  const [selectedCapsule, setSelectedCapsule] = useState(null);
  const [timeCapsules, setTimeCapsules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packageData, setPackageData] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [showNewCapsulePopup, setShowNewCapsulePopup] = useState(false);
  const [currentPackageId, setCurrentPackageId] = useState(null);
  const [userCurrentPackage, setUserCurrentPackage] = useState(null);
  
  // Yeni kapsül form state'leri
  const [email, setEmail] = useState("");
  const [newCapsuleTitle, setNewCapsuleTitle] = useState("");
  const [newCapsuleDescription, setNewCapsuleDescription] = useState("");
  const [newCapsuleContent, setNewCapsuleContent] = useState("");
  const [newCapsulePhoto, setNewCapsulePhoto] = useState(null);
  const [newCapsuleDate, setNewCapsuleDate] = useState("");
  const [newCapsuleTime, setNewCapsuleTime] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Email, 2: Görsel, 3: Başlık/Açıklama/Content, 4: Tarih/Saat
  const [isTimeCapsuleReceivingEnabled, setIsTimeCapsuleReceivingEnabled] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  
  // Zaman kapsülü gönderme linki
  const timeCapsuleLink = `${window.location.origin}/${id}/`;

  // Notiflix'i initialize et
  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  // Paket ve kullanıcı bilgilerini çek (moduleId sabit 12 - TimeCapsule)
  useEffect(() => {
    if (!id) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(id, 12);
        setPackageData(data);
        
        // Kullanıcının mevcut paketini kontrol et
        const userData = await getCurrentPackageUser(id);
        setUserCurrentPackage(userData.current_package);
        setCurrentPackageId(data.package?.id || null);
        
        // Eğer mevcut paket bu modülse, toggle'ı aktif yap
        setIsTimeCapsuleReceivingEnabled(userData.current_package === (data.package?.id || null));
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      }
    };

    fetchPackageData();
  }, [id]);

  // Zaman kapsüllerini fetch et
  useEffect(() => {
    const fetchTimeCapsules = async () => {
      try {
        setLoading(true);
        const response = await getTimeCapsuleList();
        if (response.success && response.data) {
          setTimeCapsules(response.data);
        }
      } catch (error) {
        console.error("Zaman kapsülleri alınırken hata:", error);
        Notiflix.Notify.failure("Zaman kapsülleri yüklenirken bir hata oluştu!");
      } finally {
        setLoading(false);
      }
    };

    fetchTimeCapsules();
  }, []);

  const handleBack = () => {
    setShowSingleTimeCapsule(false);
    setSelectedCapsule(null);
    // Geri döndüğünde listeyi yenile
    const refreshList = async () => {
      try {
        const response = await getTimeCapsuleList();
        if (response.success && response.data) {
          setTimeCapsules(response.data);
        }
      } catch (error) {
        console.error("Zaman kapsülleri yenilenirken hata:", error);
      }
    };
    refreshList();
  };

  const toggleTimeCapsuleReceiving = async () => {
    const newState = !isTimeCapsuleReceivingEnabled;
    
    try {
      if (newState) {
        // Aktif et
        if (!currentPackageId) {
          Notiflix.Notify.failure("Paket bilgisi bulunamadı!");
          return;
        }
        await setCurrentPackage(currentPackageId);
        setUserCurrentPackage(currentPackageId);
        setIsTimeCapsuleReceivingEnabled(true);
        Notiflix.Notify.success('Zaman kapsülü alımı aktif', {
          position: 'top-center',
          timeout: 3000,
        });
      } else {
        // Kapat
        await setCurrentPackage(3); // Modülü kapat
        setUserCurrentPackage(3);
        setIsTimeCapsuleReceivingEnabled(false);
        Notiflix.Notify.info('Zaman kapsülü alımı pasif', {
          position: 'top-center',
          timeout: 3000,
        });
      }
    } catch (error) {
      console.error("Paket durumu değiştirilirken hata:", error);
      Notiflix.Notify.failure("Bir hata oluştu!");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(timeCapsuleLink);
    Notiflix.Notify.success('Link kopyalandı!', {
      position: 'top-center',
      timeout: 2000,
    });
  };

  const shareLink = async () => {
    const shareData = {
      title: 'Zaman Kapsülü İste',
      text: 'Bana bir zaman kapsülü göndermek ister misin?',
      url: timeCapsuleLink,
    };

    // Native Share API'yi dene (mobil cihazlarda çalışır)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        Notiflix.Notify.success('Link paylaşıldı!', {
          position: 'top-center',
          timeout: 2000,
        });
      } catch (error) {
        // Kullanıcı paylaşımı iptal etti veya hata oluştu
        if (error.name !== 'AbortError') {
          console.error('Paylaşım hatası:', error);
        }
      }
    } else {
      // Native share desteklenmiyorsa linki kopyala
      copyLink();
    }
  };

  const handleNewCapsuleClick = () => {
    setShowNewCapsulePopup(true);
    setIsSent(false);
    setCurrentStep(1);
    // Formu sıfırla
    setEmail("");
    setNewCapsuleTitle("");
    setNewCapsuleDescription("");
    setNewCapsuleContent("");
    setNewCapsulePhoto(null);
    setNewCapsuleDate("");
    setNewCapsuleTime("");
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!email.trim()) {
        Notiflix.Notify.failure("Lütfen e-posta adresi giriniz!");
        return;
      }
      // Email'i kontrol etmek için kullanılıyor, ama API'ye id gönderilecek
    } else if (currentStep === 3) {
      if (!newCapsuleTitle.trim()) {
        Notiflix.Notify.failure("Lütfen başlık giriniz!");
        return;
      }
      if (!newCapsuleDescription.trim()) {
        Notiflix.Notify.failure("Lütfen açıklama giriniz!");
        return;
      }
      if (!newCapsuleContent.trim()) {
        Notiflix.Notify.failure("Lütfen içerik giriniz!");
        return;
      }
    } else if (currentStep === 4) {
      if (!newCapsuleDate || !newCapsuleTime) {
        Notiflix.Notify.failure("Lütfen tarih ve saat bilgilerini giriniz!");
        return;
      }
      // Son adımda gönder
      handleSendCapsule();
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNewCapsulePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewCapsulePhoto(file);
    }
  };

  const formatDateTime = (date, time) => {
    // date format: YYYY-MM-DD
    // time format: HH:MM
    return `${date} ${time}:00`;
  };


  const handleSendCapsule = async () => {
    setIsSending(true);

    try {
      const unlockDate = formatDateTime(newCapsuleDate, newCapsuleTime);
      
      const timeCapsuleData = {
        title: newCapsuleTitle.trim(),
        description: newCapsuleDescription.trim(),
        content: newCapsuleContent.trim(),
        unlock_date: unlockDate,
        timezone: "Europe/Istanbul",
        id: id, // Slug'dan gelen Jodiac kullanıcı ID'si
        receiver_mail: email.trim(), // Yeni kapsül popup'ında girilen email
      };

      const response = await postTimeCapsule(timeCapsuleData);

      if (response.success && response.data?.uuid) {
        const capsuleUuid = response.data.uuid;

        // Eğer fotoğraf seçilmişse, fotoğrafı yükle
        if (newCapsulePhoto) {
          try {
            await postTimeCapsulePhoto(newCapsulePhoto, capsuleUuid);
          } catch (photoError) {
            console.error("Fotoğraf yüklenirken hata:", photoError);
          }
        }

        setIsSent(true);
        // Formu temizle
        setEmail("");
        setNewCapsuleTitle("");
        setNewCapsuleDescription("");
        setNewCapsuleContent("");
        setNewCapsulePhoto(null);
        setNewCapsuleDate("");
        setNewCapsuleTime("");
      } else {
        throw new Error(response.message || "Zaman kapsülü gönderilemedi");
      }
    } catch (error) {
      console.error("Zaman kapsülü gönderilirken hata:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message ||
          error.message ||
          "Zaman kapsülü gönderilirken bir hata oluştu!"
      );
    } finally {
      setIsSending(false);
    }
  };


  const handleCapsuleClick = async (capsule) => {
    try {
      // Detaylı veriyi çek
      const response = await getTimeCapsule(capsule.uuid);
      if (response.success && response.data) {
        setSelectedCapsule(response.data);
        setShowSingleTimeCapsule(true);
      } else {
        // Eğer detaylı veri alınamazsa, mevcut veriyi kullan
        setSelectedCapsule(capsule);
        setShowSingleTimeCapsule(true);
      }
    } catch (error) {
      console.error("Zaman kapsülü detayı alınırken hata:", error);
      // Hata durumunda mevcut veriyi kullan
      setSelectedCapsule(capsule);
      setShowSingleTimeCapsule(true);
    }
  };

  const handleAcceptCapsule = async (uuid) => {
    try {
      await updateTimeCapsuleStatus(uuid, 1); // action: 1 = kabul et
      Notiflix.Notify.success("Zaman kapsülü kabul edildi!");
      // Listeyi yenile
      const response = await getTimeCapsuleList();
      if (response.success && response.data) {
        setTimeCapsules(response.data);
      }
    } catch (error) {
      console.error("Zaman kapsülü kabul edilirken hata:", error);
      Notiflix.Notify.failure("Zaman kapsülü kabul edilirken bir hata oluştu!");
    }
  };

  const handleRejectCapsule = async (uuid) => {
    try {
      await updateTimeCapsuleStatus(uuid, 3); // action: 3 = sil
      Notiflix.Notify.success("Zaman kapsülü silindi!");
      // Listeyi yenile
      const response = await getTimeCapsuleList();
      if (response.success && response.data) {
        setTimeCapsules(response.data);
      }
    } catch (error) {
      console.error("Zaman kapsülü silinirken hata:", error);
      Notiflix.Notify.failure("Zaman kapsülü silinirken bir hata oluştu!");
    }
  };

  // Eğer tek zaman kapsülü görüntüleme ekranı açıksa, o komponenti göster
  if (showSingleTimeCapsule && selectedCapsule) {
    return <SingleTimeCapsule timeCapsuleData={selectedCapsule} onBack={handleBack} />;
  }

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
              title={packageData.package?.name}
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        {/* Sağ Üst Köşe Butonları */}
        <div className="w-full px-4 mb-4 flex items-center justify-end gap-1.5 sm:gap-2">
          {/* Info İkonu */}
          <button
            onClick={() => setShowInfoPopup(true)}
            className="p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-300 text-gray-600 hover:bg-gray-200 hover:scale-105 active:scale-95"
            title="Bilgi"
          >
            <FaInfoCircle className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Zaman Kapsülü Alımı Aç/Kapa Butonu */}
          <button
            onClick={toggleTimeCapsuleReceiving}
            className={`px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center gap-2 ${
              isTimeCapsuleReceivingEnabled
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg hover:scale-105 active:scale-95'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:scale-105 active:scale-95'
            }`}
            title={isTimeCapsuleReceivingEnabled ? 'Zaman kapsülü alımını kapat' : 'Zaman kapsülü alımını aç'}
          >
            <span className="text-xs sm:text-sm font-medium">
              {isTimeCapsuleReceivingEnabled ? 'Aktif' : 'Pasif'}
            </span>
            <FaPowerOff className={`w-4 h-4 sm:w-5 sm:h-5 ${isTimeCapsuleReceivingEnabled ? '' : 'opacity-60'}`} />
          </button>

          {/* Yeni Kapsül Butonu */}
          <button
            onClick={handleNewCapsuleClick}
            className="px-4 py-2 rounded-lg sm:rounded-xl font-medium text-sm transition-all duration-300 bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] text-white hover:shadow-lg hover:scale-105 active:scale-95"
          >
            Yeni Kapsül
          </button>
        </div>

        {/* Zaman Kapsülü Gönderme Linki - Sadece aktif olduğunda göster */}
        {isTimeCapsuleReceivingEnabled && (
          <div className="px-4 sm:px-6 mb-4 sm:mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-5 shadow-lg">
              {/* Link Kutusu */}
              <div className="bg-white rounded-xl p-3 mb-3 border border-blue-200">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={timeCapsuleLink}
                    readOnly
                    className="flex-1 text-xs sm:text-sm text-gray-700 bg-transparent border-none outline-none"
                  />
                </div>
              </div>

              {/* Paylaş Butonu */}
              <button
                onClick={shareLink}
                className="w-full bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] hover:from-[#2F8894] hover:to-[#4A9BA6] text-white rounded-xl py-3 px-4 flex items-center justify-center gap-2 font-medium transition-all duration-300 hover:shadow-lg active:scale-95"
              >
                <LuRectangleHorizontal className="w-5 h-5" />
                <span className="text-sm">Arkadaşından Zaman Kapsülü İste</span>
              </button>
            </div>
          </div>
        )}

        {/* İçerik - Zaman Kapsülü Listesi */}
        <div className="mb-8 w-full px-4">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3DA7B5]"></div>
            </div>
          ) : timeCapsules.length > 0 ? (
            <div className="space-y-4 w-full">
              {timeCapsules.map((capsule, index) => {
                const isPending = capsule.request_status === "pending";
                const isLocked = capsule.status === "locked";
                const isJodiacUser = capsule.is_jodiac_user === 1;
                
                return (
                  <div 
                    key={capsule.uuid} 
                    className={`pt-4 pb-2 px-4 w-full rounded-2xl border transition-colors duration-200 ${
                      isLocked ? 'bg-gray-50' : 'bg-white'
                    } ${
                      isJodiacUser ? 'border-[#5abcc8]' : 'border-gray-100'
                    } ${!isPending ? 'cursor-pointer' : ''}`}
                    onClick={!isPending ? () => handleCapsuleClick(capsule) : undefined}
                  >
                    <div className="flex w-full items-start justify-between mb-3">
                      <div className="flex-1 text-left">
                        <h3 className="text-gray-800 font-semibold text-lg mb-1 text-left">
                          {capsule.title}
                        </h3>
                        {capsule.sender_name && (
                          <p className="text-gray-600 text-sm mb-1 text-left">
                            {capsule.sender_name}
                          </p>
                        )}
                        {isJodiacUser && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#5abcc8] text-white mb-1">
                            Jodiac Kullanıcısı
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {isPending ? (
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleAcceptCapsule(capsule.uuid)}
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors"
                            >
                              Kabul Et
                            </button>
                            <button
                              onClick={() => handleRejectCapsule(capsule.uuid)}
                              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
                            >
                              Sil
                            </button>
                          </div>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isLocked
                              ? 'bg-red-100 text-red-600' 
                              : 'bg-green-100 text-green-600'
                          }`}>
                            {isLocked ? 'Kilitli' : 'Açık'}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Kalan süre bilgisi */}
                    {capsule.remaining_time && !capsule.remaining_time.is_unlocked && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Açılma: {new Date(capsule.unlock_date).toLocaleDateString('tr-TR')}</span>
                          <span className="text-[#3DA7B5] font-medium">
                            {capsule.remaining_time.days > 0 
                              ? `${capsule.remaining_time.days} gün kaldı`
                              : capsule.remaining_time.hours > 0
                              ? `${capsule.remaining_time.hours} saat kaldı`
                              : `${capsule.remaining_time.minutes} dakika kaldı`
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">
                Henüz zaman kapsülünüz bulunmuyor.
              </p>
            </div>
          )}
        </div>


        {/* Popup Modal - Yeni Kapsül */}
        {showNewCapsulePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isSent && setShowNewCapsulePopup(false)}>
            <div className="bg-white rounded-2xl p-5 mx-4 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              {!isSent ? (
                <>
                  {/* Step Indicator */}
                  <div className="flex items-center justify-center mb-4">
                    {[1, 2, 3, 4].map((step) => (
                      <div key={step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          step === currentStep
                            ? 'bg-[#3DA7B5] text-white'
                            : step < currentStep
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          {step < currentStep ? '✓' : step}
                        </div>
                        {step < 4 && (
                          <div className={`w-8 h-0.5 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Step 1: E-posta */}
                  {currentStep === 1 && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-800 text-center">
                        Jodiac Kullanıcısı E-posta
                      </h2>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent text-gray-800"
                        placeholder="ornek@email.com"
                        autoFocus
                      />
                    </div>
                  )}

                  {/* Step 2: Görsel (Opsiyonel) */}
                  {currentStep === 2 && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-800 text-center">
                        Görsel Ekle (Opsiyonel)
                      </h2>
                      <div className="relative w-full h-40 rounded-lg bg-[#F0F3F6] flex items-center justify-center border-2 border-dashed border-gray-300">
                        {newCapsulePhoto ? (
                          <div className="relative w-full h-full">
                            <img
                              src={URL.createObjectURL(newCapsulePhoto)}
                              alt="Seçilen fotoğraf"
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <button
                              onClick={() => setNewCapsulePhoto(null)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="text-center">
                            <svg
                              className="w-10 h-10 text-gray-400 mx-auto mb-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <p className="text-sm text-gray-500">Görsel eklemek için tıklayın</p>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleNewCapsulePhotoChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Başlık, Açıklama ve İçerik */}
                  {currentStep === 3 && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-800 text-center">
                        Başlık, Açıklama ve İçerik
                      </h2>
                      <input
                        type="text"
                        value={newCapsuleTitle}
                        onChange={(e) => setNewCapsuleTitle(e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent text-gray-800"
                        placeholder="Başlık"
                        autoFocus
                      />
                      <textarea
                        value={newCapsuleDescription}
                        onChange={(e) => setNewCapsuleDescription(e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent text-gray-800 resize-none"
                        rows="3"
                        placeholder="Açıklama"
                      />
                      <textarea
                        value={newCapsuleContent}
                        onChange={(e) => setNewCapsuleContent(e.target.value)}
                        className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent text-gray-800 resize-none"
                        rows="4"
                        placeholder="İçerik"
                      />
                    </div>
                  )}

                  {/* Step 4: Tarih ve Saat */}
                  {currentStep === 4 && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-800 text-center">
                        Açılma Tarihi ve Saati
                      </h2>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tarih
                        </label>
                        <input
                          type="date"
                          value={newCapsuleDate}
                          onChange={(e) => setNewCapsuleDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent text-gray-800 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:invert-0 [&::-webkit-calendar-picker-indicator]:brightness-0"
                          style={{
                            colorScheme: 'light'
                          }}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Saat
                        </label>
                        <input
                          type="time"
                          value={newCapsuleTime}
                          onChange={(e) => setNewCapsuleTime(e.target.value)}
                          className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent text-gray-800 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:invert-0 [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-clock-picker-button]:cursor-pointer [&::-webkit-clock-picker-button]:opacity-100 [&::-webkit-clock-picker-button]:invert-0 [&::-webkit-clock-picker-button]:brightness-0"
                          style={{
                            colorScheme: 'light'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex gap-3 mt-6">
                    {currentStep > 1 && (
                      <button
                        onClick={handlePrevStep}
                        className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      >
                        Geri
                      </button>
                    )}
                    <button
                      onClick={() => setShowNewCapsulePopup(false)}
                      className={currentStep === 1 ? "flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" : "px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"}
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleNextStep}
                      disabled={isSending}
                      className="flex-1 px-4 py-2 bg-[#3DA7B5] text-white rounded-lg font-medium hover:bg-[#2A8B96] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSending ? (
                        <span className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Gönderiliyor...
                        </span>
                      ) : currentStep === 4 ? (
                        "Gönder"
                      ) : (
                        "İleri"
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    İstek Gönderildi!
                  </h2>
                  <p className="text-gray-600 mb-6">
                    İlgili jodiaclıya zaman kapsülü isteği başarıyla gönderildi.
                  </p>
                  <button
                    onClick={() => {
                      setShowNewCapsulePopup(false);
                      setIsSent(false);
                    }}
                    className="px-6 py-2 bg-[#3DA7B5] text-white rounded-lg font-medium hover:bg-[#2A8B96] transition-colors"
                  >
                    Tamam
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Popup Modal */}
        {showInfoPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowInfoPopup(false)}>
            <div className="bg-white rounded-2xl p-6 mx-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Zaman Kapsülünü aktif ettiğinizde ve başka bir cihaz bilekliğinizi okuttuğunda size zaman kapsülü bırakabilecektir.
              </p>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowInfoPopup(false)}
                  className="px-6 py-2 bg-[#3DA7B5] text-white rounded-lg font-medium hover:bg-[#2A8B96] transition-colors"
                >
                  Tamam
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="h-20"></div>
      </div>
    </div>
  );
}